import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { TeamItemField } from '@makyoapp/makyo-common'
import { WrappedPagination } from '@makyoapp/makyo-ui'

import { queryKeys } from '@/lib/react-query'
import { useGetOrgTeams } from '@/resolvers/org/query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgsContext } from '@/context/OrgsContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { DEFAULT_ROW_PER_PAGE } from '@/shared/constants'
import { useQueryParam } from '@/shared/makyo-hooks'
import { WrapperProps } from '@/shared/routes'
import Layout from '@/components/Layout'
import { PageUnauthorized } from '@/components/PageUnauthorized'

import AddNewTeamModal from './components/AddNewTeamModal'
import TeamTable from './components/TeamTable'

/**
 * render list team
 */
function OrgDetailTeamList({ title }: WrapperProps) {
  const queryClient = useQueryClient()

  // Access current org
  const { currentOrg } = useCurrentOrgContext()
  const currentOrgId = currentOrg?.id

  // Access Org Perms
  const { orgsPerms } = useOrgsContext()

  // Validate permission to add new team
  const canAddNewTeam = currentOrgId ? orgsPerms[currentOrgId]?.[OrgPermission.TeamsCreate] : false

  // access query params
  const { isLoading, setIsLoading, debounceSearch, search, setSearch, page, setPage, resetParams } =
    useQueryParam<TeamItemField>()

  const [, updateState] = useState<null | object>(null)
  const forceUpdate = useCallback(() => updateState({}), [])

  // access team list from sdk
  const { data: teamsData, isLoading: listLoading } = useGetOrgTeams({
    search: debounceSearch,
  })

  // derived state
  const allItems = useMemo(
    () => ({
      items: teamsData || [],
      totalCount: teamsData?.length ?? 0,
    }),
    [teamsData]
  )

  const currentOrgTeams = useMemo(() => {
    const offset = (page - 1) * DEFAULT_ROW_PER_PAGE
    return allItems.items.slice(offset, offset + DEFAULT_ROW_PER_PAGE)
  }, [page, allItems])

  // Callback function to run after a team update
  const onAfterUpdate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [queryKeys.orgTeams, currentOrgId],
    })
    forceUpdate()
  }, [queryClient, currentOrgId, forceUpdate])

  // Handler for changing the page
  const handlePageChange = (_from: number, _to: number, newPage: number) => setPage(newPage)

  return (
    <Layout
      title={title}
      search={search}
      onSearch={setSearch}
      onClearSearch={resetParams}
      actions={
        // Validation to add new team
        canAddNewTeam ? (
          // Render Add new Team Modal
          <AddNewTeamModal onAfterUpdate={onAfterUpdate} currentOrgId={currentOrgId} />
        ) : undefined
      }
    >
      {/* Render Team Table */}
      <TeamTable
        setIsLoading={setIsLoading}
        isLoading={isLoading || listLoading}
        onAfterUpdate={onAfterUpdate}
        teams={currentOrgTeams}
      />
      {/* Render Pagination */}
      <WrappedPagination
        id='team-pg'
        perPage={DEFAULT_ROW_PER_PAGE}
        totalCount={allItems.totalCount}
        page={page}
        onChange={handlePageChange}
      />
    </Layout>
  )
}

/**
 * permission check on list team page
 */
export default function OrgDetailTeamIndex({ title }: WrapperProps) {
  const canViewTeams = useOrgPerm(OrgPermission.TeamsViewList)

  // Validation to Render Team Data
  return canViewTeams ? (
    <OrgDetailTeamList title={title} />
  ) : !canViewTeams ? (
    //Render Team Data
    <Layout title={title}>
      {/* Render unauthorized page if user have no permission */}
      <PageUnauthorized />
    </Layout>
  ) : null
}
